import React, { useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Typography,
    useTheme,
} from '@mui/material'
import GetAppIcon from '@mui/icons-material/GetApp'

import { FlexDiv } from '../../components'
import { ResponsivePageContainer } from '../../components/PageContainers'
import { useInspectionHub } from '../../hooks/useInspectionHub'
import { useInspectionReport } from '../../hooks/useInspectionReport'
import { RootState } from '../../store'
import { MoveInSection } from './MoveInSection'
import { MoveOutSection } from './MoveOutSection'
import { PropertyInspectionsSection } from './PropertyInspectionsSection'
import { getSectionExpansionDefaults } from './sectionExpansion'

export const Inspections = () => {
    const theme = useTheme()
    const selectedLease = useSelector(
        (state: RootState) => state.lease.selectedLease,
    )
    const leaseId = selectedLease?.id
    const apartmentId = selectedLease?.apartment_id

    const moveInRef = useRef<HTMLDivElement>(null)
    const propertyRef = useRef<HTMLDivElement>(null)
    const moveOutRef = useRef<HTMLDivElement>(null)

    const { hubData, isLoading, getInspectionHub } = useInspectionHub(
        leaseId,
        apartmentId,
    )
    const { isDownloading, downloadReport } = useInspectionReport(
        leaseId,
        apartmentId,
    )

    useEffect(() => {
        getInspectionHub()
    }, [leaseId, apartmentId])

    const expansionDefaults = useMemo(
        () => (hubData ? getSectionExpansionDefaults(hubData) : null),
        [hubData],
    )

    const scrollToSection = (ref: React.RefObject<HTMLDivElement | null>) => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    if (!leaseId || !apartmentId) {
        return (
            <ResponsivePageContainer
                title="Inspections"
                subtitle="Your complete inspection history for this lease."
            >
                <Typography color="textSecondary">
                    Select a lease to view inspections.
                </Typography>
            </ResponsivePageContainer>
        )
    }

    return (
        <ResponsivePageContainer
            title="Inspections"
            subtitle="Your complete inspection history for this lease."
        >
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    maxWidth: '800px',
                    width: '100%',
                }}
            >
                <Button
                    variant="outlined"
                    startIcon={
                        isDownloading ? (
                            <CircularProgress size={18} sx={{ color: '#fff' }} />
                        ) : (
                            <GetAppIcon />
                        )
                    }
                    onClick={downloadReport}
                    aria-busy={isDownloading}
                    sx={{
                        alignSelf: 'flex-start',
                        textTransform: 'none',
                        borderColor: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        opacity: 1,
                        pointerEvents: isDownloading ? 'none' : 'auto',
                        '&.Mui-disabled': {
                            opacity: 1,
                            color: '#fff',
                            borderColor: 'rgba(255,255,255,0.2)',
                        },
                    }}
                >
                    {isDownloading
                        ? 'Generating report...'
                        : 'Download full inspection report'}
                </Button>

                {hubData && (
                    <Box
                        sx={{
                            display: 'flex',
                            gap: 1,
                            flexWrap: 'wrap',
                        }}
                    >
                        {[
                            { label: 'Move-In', ref: moveInRef },
                            { label: 'Property', ref: propertyRef },
                            { label: 'Move-Out', ref: moveOutRef },
                        ].map(({ label, ref }) => (
                            <Chip
                                key={label}
                                label={label}
                                size="small"
                                clickable
                                onClick={() => scrollToSection(ref)}
                                sx={{
                                    fontWeight: 600,
                                    bgcolor: 'rgba(255,255,255,0.08)',
                                    color: 'rgba(255,255,255,0.75)',
                                    '&:hover': {
                                        bgcolor: 'rgba(255,255,255,0.14)',
                                    },
                                }}
                            />
                        ))}
                    </Box>
                )}

                {isLoading ? (
                    <FlexDiv
                        style={{
                            justifyContent: 'center',
                            padding: theme.spacing(4),
                            width: '100%',
                        }}
                    >
                        <CircularProgress
                            size={40}
                            style={{ color: theme.palette.primary.main }}
                        />
                    </FlexDiv>
                ) : hubData && expansionDefaults ? (
                    <>
                        <Box ref={moveInRef}>
                            <MoveInSection
                                moveIn={hubData.move_in}
                                leaseId={leaseId}
                                defaultExpanded={expansionDefaults.moveIn}
                                onRefresh={getInspectionHub}
                            />
                        </Box>
                        <Box ref={propertyRef}>
                            <PropertyInspectionsSection
                                propertyInspections={hubData.property_inspections}
                                defaultExpanded={expansionDefaults.property}
                            />
                        </Box>
                        <Box ref={moveOutRef}>
                            <MoveOutSection
                                moveOut={hubData.move_out}
                                defaultExpanded={expansionDefaults.moveOut}
                            />
                        </Box>
                    </>
                ) : (
                    <Typography color="textSecondary">
                        Unable to load inspections.
                    </Typography>
                )}
            </Box>
        </ResponsivePageContainer>
    )
}
